import {
  tag_colorTagDefaultBackground,
  tag_colorTagDefaultText,
  tag_colorTagPendingBackground,
  tag_colorTagPendingText,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_sm,
} from 'ext-ui/style/typography'
import React from 'react'
import ReceiptReconciledCheckIcon from 'src/assets/svgs/ReceiptAgentReconciledCheckIcon.svg'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptRecoveryLifecycleState } from 'src/models/transaction.models'
import styled from 'styled-components'

interface ReceiptAgentStatusCellProps {
  status?: ReceiptRecoveryLifecycleState
}

type ReceiptAgentStatusPillColor = 'default' | 'pending'

const receiptAgentStatusPillColors: Record<
  ReceiptAgentStatusPillColor,
  { background: string; text: string }
> = {
  default: {
    background: tag_colorTagDefaultBackground,
    text: tag_colorTagDefaultText,
  },
  pending: {
    background: tag_colorTagPendingBackground,
    text: tag_colorTagPendingText,
  },
}

const ReceiptAgentStatusPill = styled.span.attrs({
  role: 'status',
})<{
  $color: ReceiptAgentStatusPillColor
}>`
  display: flex;
  height: ${spacing(5)};
  padding: 0 ${spacing(2)};
  justify-content: center;
  align-items: center;
  gap: ${spacing(1)};
  border-radius: ${cornerRadius};
  background: ${({ $color }) =>
    receiptAgentStatusPillColors[$color].background};
  color: ${({ $color }) => receiptAgentStatusPillColors[$color].text};
  ${font_sm};
  font-family: ${font_family_weight_medium};
`

const ReceiptReconciledStatus = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing(1)};
`

const ReceiptReconciledLabel = styled.p`
  margin: 0;
  color: #697080;
  ${font_sm};
  font-family: ${font_family_weight_regular};
  white-space: nowrap;
`

export default function ReceiptAgentStatusCell({
  status,
}: ReceiptAgentStatusCellProps): React.ReactElement | null {
  const translate = useTranslate()

  switch (status) {
    case ReceiptRecoveryLifecycleState.NeedsReview:
      return (
        <ReceiptAgentStatusPill $color="pending">
          {translate('Needs Review')}
        </ReceiptAgentStatusPill>
      )
    case ReceiptRecoveryLifecycleState.WaitingOnSpender:
      return (
        <ReceiptAgentStatusPill $color="default">
          {translate('Waiting on Spender')}
        </ReceiptAgentStatusPill>
      )
    case ReceiptRecoveryLifecycleState.ReceiptReconciled:
      return (
        <ReceiptReconciledStatus data-testid="receipt-agent-reconciled-status">
          <img
            alt=""
            data-testid="receipt-agent-reconciled-check"
            src={ReceiptReconciledCheckIcon}
            width={16}
            height={16}
          />
          <ReceiptReconciledLabel>
            {translate('Receipt Reconciled')}
          </ReceiptReconciledLabel>
        </ReceiptReconciledStatus>
      )
    default:
      return null
  }
}
